/**
 * N2MethodologyPage — trust page. For each model: what it measures, the
 * framework, where it works, where it fails, historical hit rate, and what
 * we deliberately don't publish.
 *
 * Long-form editorial layout. Serif body for readability, sans for labels.
 * No sales pitch here — this is the "before you trust the readings, know
 * how they're made" page.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import { Eyebrow, PageTitle, PageLede, SectionLabel } from './N2Primitives';

export const N2MethodologyPage: React.FC = () => (
  <N2Frame page="methodology">
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '64px 32px 40px' }}>
      <Eyebrow>Methodology</Eyebrow>
      <PageTitle italic="works.">
        How the research
      </PageTitle>
      <PageLede>
        Notches organizes a research and trading platform around three
        stages — Understand, Evaluate, Execute — and publishes measurements
        from a set of options-market models. Below: how a Researched
        Opportunity is produced end-to-end, what each model measures, and
        the boundaries around interpretation.
      </PageLede>

      <WorkflowSection />

      <ModelWriteup
        model="GEX"
        subtitle="Gamma Exposure"
        measures="The aggregate gamma position of options dealers, expressed in dollars per 1% index move."
        framework={[
          'Start with the full options chain — every strike, every expiry, at the reporting timestamp.',
          'For each position, the dealer\'s counterparty gamma is inferred from open interest and standard dealer-book assumptions.',
          'Sign convention: negative GEX means dealers are net short-gamma. Their hedging amplifies moves. Positive GEX dampens.',
          'The notch fires when the aggregate flips sign or breaches a strike-level threshold that historically preceded a regime shift.',
        ]}
        worksIn="Short-gamma regimes with clear dealer positioning — SPX and QQQ where flow data is dense enough to support the inference."
        failsIn="Extreme low-volume days, expiration Fridays where the position rolls, and single-stock names where dealer positioning is noisy."
        weDoNotPublish="The exact positioning inference model. The dealer-book assumptions are stated in general terms; the specific parameter values are not published because we intentionally do not want them reverse-engineered by market makers."
      />

      <ModelWriteup
        model="COT"
        subtitle="Commitments of Traders"
        measures="Weekly futures positioning by trader category, normalized as a z-score against a 156-week rolling window."
        framework={[
          'Source data is the CFTC Commitments of Traders report, released each Friday for the prior Tuesday.',
          'Two report formats are read: Traditional (large speculators, commercials, non-reportables) and Disaggregated (managed money, producers, swap dealers, other reportables).',
          'For each category, net positioning is normalized to a z-score across the trailing 156 weeks (3 years). The notch fires when a category crosses a ±2σ threshold.',
          'Directional bias derives from the historical relationship between the category\'s extreme positioning and forward returns.',
        ]}
        worksIn="Slow-moving positioning shifts in commodity and index futures. Comms in energy, metals, and grains have the cleanest historical relationship."
        failsIn="News-driven weeks where positioning inverts inside a single reporting window. Also, the report has a 3-day publication lag — the notch is stale relative to intraday moves."
        weDoNotPublish="Which futures products we consider primary vs. supporting evidence, and the exact z-score thresholds per product family. These are calibrated per-product and change with liquidity."
      />

      <ModelWriteup
        model="IV"
        subtitle="Implied Volatility Percentile"
        measures="The current implied volatility of an underlying, expressed as the percentage of the trailing 252 sessions on which IV was lower than today's reading."
        framework={[
          'For each underlying, pull the ATM 30-day IV every session close.',
          'Compute IV Percentile: the share of the trailing 252 sessions on which IV was below the current reading. IVP 91 means IV is above the level seen on 91% of the past year — distinct from IV Rank, which measures where today sits between the 12-month IV low and high.',
          'The notch fires at extreme percentiles (IVP > 80 for mean-reversion setups, IVP < 20 for long-vol setups).',
          'A regime overlay conditions the notch: mean-reversion setups get de-weighted in trending-vol environments per a backtested classifier.',
        ]}
        worksIn="Liquid single-name options in calm regimes. SPX and QQQ during quiet macro periods."
        failsIn="News-cluster weeks where IV moves ahead of realized. Also, small-cap names where the 252-day IV distribution is thin."
        weDoNotPublish="The regime-overlay classifier. The features that decide whether we're in a trending-vol or mean-reverting-vol environment are proprietary and change quarterly."
      />

      <ModelWriteup
        model="AVWAP"
        subtitle="Anchored VWAP"
        measures="Volume-weighted average price anchored to a significant reference event — a prior high, a prior low, an earnings date, a policy announcement."
        framework={[
          'Anchor selection is rules-based, not discretionary. Anchors include: local swing highs/lows, gap opens, earnings days, and quarter opens.',
          'AVWAP is computed from the anchor forward. Bounces off AVWAP in a trending regime constitute a notch; rejections in a range regime constitute the opposite notch.',
          'The regime tag ("trending" vs. "range") is set by an independent classifier that reads ADX, ATR-normalized displacement, and 20-day slope.',
          'The notch fires only when price interacts with the AVWAP inside the regime the anchor was set for.',
        ]}
        worksIn="Trending regimes with clean anchor references. Index futures during established directional runs."
        failsIn="Chop. The classifier calls chop a range and the bounces read as rejections, but chop by definition doesn\'t honor either side."
        weDoNotPublish="The regime classifier feature set and thresholds. Publishing them would let the anchor-selection rules be pre-empted."
      />

      <BoundarySection />
    </div>
  </N2Frame>
);

// ---- Model writeup component ----------------------------------------------

const ModelWriteup: React.FC<{
  model: string;
  subtitle: string;
  measures: string;
  framework: string[];
  worksIn: string;
  failsIn: string;
  weDoNotPublish: string;
}> = ({ model, subtitle, measures, framework, worksIn, failsIn, weDoNotPublish }) => (
  <section style={{
    marginTop: 72, paddingTop: 40, borderTop: `2px solid ${palette.line}`,
  }}>
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 20, marginBottom: 8,
    }}>
      <h2 style={{
        fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 44,
        color: palette.ink, letterSpacing: '-0.015em', margin: 0,
      }}>{model}</h2>
      <span style={{
        fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22,
        color: palette.goldDeep,
      }}>{subtitle}</span>
    </div>
    <p style={{
      fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 19,
      lineHeight: 1.5, color: palette.ink2, margin: '0 0 32px',
      textWrap: 'balance' as React.CSSProperties['textWrap'],
    }}>{measures}</p>

    <Row label="Framework">
      <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {framework.map((step, i) => (
          <li key={i} style={{
            fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.65,
            color: palette.ink2, padding: '8px 0 8px 32px',
            position: 'relative',
          }}>
            <span style={{
              position: 'absolute', left: 0, top: 8,
              fontFamily: 'var(--serif)', fontStyle: 'italic',
              fontSize: 15, color: palette.goldDeep,
              fontVariantNumeric: 'tabular-nums',
            }}>{i + 1}.</span>
            {step}
          </li>
        ))}
      </ol>
    </Row>

    <Row label="Where it works">
      <RowBody>{worksIn}</RowBody>
    </Row>

    <Row label="Where it fails">
      <RowBody>{failsIn}</RowBody>
    </Row>

    <Row label="Recorded outcomes">
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.65,
        color: palette.ink2, margin: 0,
      }}>
        Live outcomes for every published reading are recorded on the{' '}
        <Link to="/scorecard" style={{
          color: palette.goldDeep, textDecoration: 'underline',
          textDecorationColor: palette.line, textUnderlineOffset: 3,
        }}>
          public scorecard
        </Link>
        . No summary statistic is published here until enough live readings
        accumulate to make one honest.
      </p>
    </Row>

    <Row label="What we don't publish">
      <RowBody>{weDoNotPublish}</RowBody>
    </Row>
  </section>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: '160px 1fr', gap: 32,
    padding: '20px 0', borderTop: `1px dashed ${palette.line}`,
    alignItems: 'baseline',
  }} className="n2-methodology-row">
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
    }}>{label}</div>
    <div>{children}</div>
  </div>
);

const RowBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{
    fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.65,
    color: palette.ink2, margin: 0,
  }}>{children}</p>
);

// ---- Workflow section — connects the framework to how research is made ---

const WorkflowSection: React.FC = () => (
  <section style={{ marginTop: 64 }}>
    <SectionLabel left="How a Researched Opportunity is produced" right="market inputs → published opportunity" />
    <ol style={{ margin: '28px 0 0', padding: 0, listStyle: 'none' }}>
      <WorkflowStage
        stage="Market inputs"
        body="Options chains, futures positioning data, and price/volume histories are collected on a scheduled cadence. Sources are public where possible (CFTC, exchange data) and licensed feeds elsewhere. Data quality checks run at ingest; a stale or incomplete input suppresses downstream candidates for the affected symbol."
        limits="No proprietary tick data, no order-flow leaks, no material non-public information. If a feed drops during the day, the affected model is marked stale rather than filled with an assumed value."
      />
      <WorkflowStage
        stage="Analysis"
        body="The four options-market models below are computed independently — GEX, COT, IV Percentile, and AVWAP. Each produces its own measurement per symbol per session. Where the underlying rule set is deterministic (GEX aggregation, COT z-scoring, IV percentile, AVWAP anchor selection), the calculation runs as code, not a language model."
        limits="Model measurements describe what the market did or how it is positioned. They are not forecasts, and their historical relationships to forward returns are only as stable as the regime they were measured in."
      />
      <WorkflowStage
        stage="AI evaluation (bounded)"
        body="For a subset of candidates, an AI evaluation layer scores structure and risk against a defined rubric — the same rubric across all candidates so scores are comparable. AI does not decide whether an opportunity is published; it produces one input among several that a rules-based publisher weighs. Where AI is used, the score is labelled as an analytical rating, not a probability of profit."
        limits="An AI score is a comparative measurement, not a demonstrated probability of profit. It cannot replace a customer's own judgment about position sizing, portfolio fit, or timing."
      />
      <WorkflowStage
        stage="Risk assessment"
        body="Each candidate is stress-checked against volatility, liquidity, and event risk. Underlyings with thin option chains, upcoming binary events, or IV disconnected from realized are down-weighted or dropped."
        limits="Risk assessment applies to the setup, not the customer's portfolio. Notches does not know the customer's other positions or account size, and position sizing remains the customer's decision."
      />
      <WorkflowStage
        stage="Published opportunity"
        body="Candidates that clear the analysis, evaluation, and risk checks are published on the corresponding Researched Opportunities board (SPX, SPY, QQQ, IWM, ES, MES, GC, CL, or Premier's equities feed). Each opportunity shows the underlying setup, the analytical rating, key risks, and — where applicable — a suggested structure and range. Automation applies only where a supported engine exists for that strategy; not every published opportunity has an automated path."
        limits="Publishing does not equal endorsement. Every reading is a measurement; interpretation and action are the customer's."
      />
    </ol>
  </section>
);

const WorkflowStage: React.FC<{ stage: string; body: string; limits: string }> = ({
  stage, body, limits,
}) => (
  <li style={{
    borderTop: `1px dashed ${palette.line}`,
    padding: '20px 0',
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: 32,
    alignItems: 'baseline',
  }} className="n2-methodology-row">
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
    }}>{stage}</div>
    <div>
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.65,
        color: palette.ink2, margin: 0,
      }}>{body}</p>
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.6,
        color: palette.ink3, margin: '10px 0 0', fontStyle: 'italic',
      }}>{limits}</p>
    </div>
  </li>
);

// ---- Boundary section -----------------------------------------------------

const BoundarySection: React.FC = () => (
  <section style={{ marginTop: 96 }}>
    <SectionLabel left="Regulatory boundary" right="what Notches is not" />
    <div style={{
      marginTop: 32, padding: '32px 36px',
      background: palette.paper2, border: `1px solid ${palette.line}`,
      borderRadius: 4,
    }}>
      <p style={{
        fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 18,
        lineHeight: 1.5, color: palette.ink2, margin: '0 0 20px',
      }}>
        Notches publishes measurements. It does not tell you what to do with
        them.
      </p>
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.7,
        color: palette.ink2, margin: 0, maxWidth: '62ch',
      }}>
        WFS is not a registered investment adviser. Nothing on this site — no
        letter, no scorecard entry, no methodology note — is personalized
        investment advice. Every reading is a measurement of what the market
        is doing; interpretation and action are yours.
      </p>
    </div>
  </section>
);

export default N2MethodologyPage;
